import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ZoomIn, ZoomOut, Maximize2, ChevronLeft, ChevronRight, Download, FileText, Loader2, AlertTriangle, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';

const ZOOMS = [0.5, 0.75, 1, 1.25, 1.5, 2, 3];

const guessFilename = (doc, contentType) => {
  if (doc.filename) return doc.filename;
  const fromUrl = decodeURIComponent(doc.url.split('?')[0].split('/').pop() || '');
  if (/\.[a-z0-9]{2,5}$/i.test(fromUrl)) return fromUrl;
  const ext = contentType?.includes('pdf') ? 'pdf' : contentType?.split('/')[1] || 'file';
  return `${doc.title || 'document'}.${ext}`;
};

const ToolbarButton = ({ onClick, disabled, title, children, testId }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    title={title}
    aria-label={title}
    className="flex h-9 w-9 items-center justify-center rounded-full text-white/75 transition-colors hover:bg-white/10 hover:text-white disabled:opacity-30 disabled:hover:bg-transparent"
    data-testid={testId}
  >
    {children}
  </button>
);

export const DocumentViewer = ({ doc, onClose }) => {
  const [blobUrl, setBlobUrl] = useState(null);
  const [blob, setBlob] = useState(null);
  const [pdfData, setPdfData] = useState(null);
  const [contentType, setContentType] = useState('');
  const [error, setError] = useState(null);
  const [numPages, setNumPages] = useState(0);
  const [page, setPage] = useState(1);
  const [zoomIdx, setZoomIdx] = useState(2);
  const [fitWidth, setFitWidth] = useState(true);
  const [containerWidth, setContainerWidth] = useState(0);
  const containerRef = useRef(null);
  const pageRefs = useRef({});

  useEffect(() => {
    if (!doc) return undefined;
    let cancelled = false;
    let objectUrl = null;
    setBlobUrl(null); setBlob(null); setPdfData(null); setError(null); setNumPages(0); setPage(1); setZoomIdx(2); setFitWidth(true);
    const sameOrigin = doc.url.startsWith(process.env.REACT_APP_BACKEND_URL) || doc.url.startsWith('/');
    axios.get(doc.url, { responseType: 'blob', withCredentials: sameOrigin })
      .then(async (res) => {
        if (cancelled) return;
        const type = res.headers['content-type'] || res.data.type || '';
        const pdf = type.includes('pdf') || /\.pdf$/i.test(doc.url.split('?')[0]);
        // pdf.js gets raw bytes (no blob: URL network stream), so closing mid-load can't throw
        const data = pdf ? { data: new Uint8Array(await res.data.arrayBuffer()) } : null;
        if (cancelled) return;
        objectUrl = URL.createObjectURL(res.data);
        setBlob(res.data); setContentType(type); setPdfData(data); setBlobUrl(objectUrl);
      })
      .catch((err) => {
        if (cancelled) return;
        const status = err.response?.status;
        setError(status === 403 ? "You don't have access to this document." : status === 404 ? 'Document not found.' : sameOrigin ? 'Unable to load this document.' : 'EXTERNAL');
      });
    return () => { cancelled = true; if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [doc]);

  useEffect(() => {
    if (!doc) return undefined;
    const el = containerRef.current;
    if (!el) return undefined;
    const ro = new ResizeObserver(([entry]) => setContainerWidth(entry.contentRect.width));
    ro.observe(el);
    setContainerWidth(el.clientWidth);
    const onKey = (e) => { if (e.key === 'Escape') { e.stopPropagation(); onClose(); } };
    window.addEventListener('keydown', onKey, true);
    return () => { ro.disconnect(); window.removeEventListener('keydown', onKey, true); };
  }, [doc, blobUrl, onClose]);

  if (!doc) return null;

  const isPdf = contentType.includes('pdf') || /\.pdf$/i.test(doc.url.split('?')[0]);
  const isImage = contentType.startsWith('image/');
  const filename = guessFilename(doc, contentType);
  const zoom = ZOOMS[zoomIdx];
  const pageWidth = fitWidth ? Math.max(280, Math.min(containerWidth - 32, 1100)) : undefined;

  const download = () => {
    if (!blob) return;
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = filename;
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 2000);
    toast.success('Download started');
  };

  const goTo = (n) => {
    const target = Math.min(Math.max(1, n), numPages || 1);
    setPage(target);
    pageRefs.current[target]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const onScroll = () => {
    const el = containerRef.current;
    if (!el || !numPages) return;
    const mid = el.scrollTop + el.clientHeight / 3;
    let current = 1;
    Object.entries(pageRefs.current).forEach(([n, node]) => {
      if (node && node.offsetTop <= mid) current = Number(n);
    });
    setPage(current);
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] flex flex-col bg-[#0B1437]"
        data-testid="document-viewer"
        role="dialog"
        aria-modal="true"
      >
        {/* Toolbar */}
        <div className="flex items-center gap-2 border-b border-white/10 bg-[#111C44] px-3 py-2 sm:px-5">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-white/10">
            <FileText className="h-4 w-4 text-[#23ACD5]" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="truncate font-['Manrope'] text-sm font-extrabold text-white" data-testid="document-viewer-title">{doc.title}</p>
            <p className="truncate text-[11px] text-white/45">{filename}</p>
          </div>

          {isPdf && numPages > 0 && (
            <div className="hidden items-center gap-1 rounded-full bg-white/5 px-1 sm:flex" data-testid="document-viewer-pager">
              <ToolbarButton onClick={() => goTo(page - 1)} disabled={page <= 1} title="Previous page" testId="document-viewer-prev"><ChevronLeft className="h-4 w-4" /></ToolbarButton>
              <span className="min-w-[64px] text-center text-xs font-bold text-white/80" data-testid="document-viewer-page-indicator">{page} / {numPages}</span>
              <ToolbarButton onClick={() => goTo(page + 1)} disabled={page >= numPages} title="Next page" testId="document-viewer-next"><ChevronRight className="h-4 w-4" /></ToolbarButton>
            </div>
          )}

          {(isPdf || isImage) && blobUrl && (
            <div className="hidden items-center gap-0.5 rounded-full bg-white/5 px-1 sm:flex">
              <ToolbarButton onClick={() => { setFitWidth(false); setZoomIdx((z) => Math.max(0, z - 1)); }} disabled={!fitWidth && zoomIdx === 0} title="Zoom out" testId="document-viewer-zoom-out"><ZoomOut className="h-4 w-4" /></ToolbarButton>
              <span className="hidden min-w-[44px] text-center text-xs font-bold text-white/80 sm:block" data-testid="document-viewer-zoom-level">{fitWidth ? 'Fit' : `${Math.round(zoom * 100)}%`}</span>
              <ToolbarButton onClick={() => { setFitWidth(false); setZoomIdx((z) => Math.min(ZOOMS.length - 1, z + 1)); }} disabled={!fitWidth && zoomIdx === ZOOMS.length - 1} title="Zoom in" testId="document-viewer-zoom-in"><ZoomIn className="h-4 w-4" /></ToolbarButton>
              <ToolbarButton onClick={() => setFitWidth(true)} title="Fit to width" testId="document-viewer-fit"><Maximize2 className="h-4 w-4" /></ToolbarButton>
            </div>
          )}

          <button
            type="button"
            onClick={download}
            disabled={!blob}
            className="flex items-center gap-2 rounded-full bg-gradient-to-r from-[#23ACD5] to-[#00E5FF] px-3 py-2 text-xs font-extrabold text-[#06263A] shadow-[0_6px_20px_rgba(35,172,213,0.4)] transition-transform hover:scale-[1.02] disabled:opacity-50 sm:px-4"
            data-testid="document-viewer-download"
          >
            <Download className="h-4 w-4" /><span className="hidden sm:inline">Download</span>
          </button>
          <ToolbarButton onClick={onClose} title="Close" testId="document-viewer-close"><X className="h-5 w-5" /></ToolbarButton>
        </div>

        {/* Body */}
        <div ref={containerRef} onScroll={onScroll} className="flex-1 overflow-auto overscroll-contain p-3 sm:p-6" data-testid="document-viewer-body">
          {error ? (
            <div className="mx-auto mt-24 max-w-sm rounded-2xl border border-white/10 bg-white/5 p-8 text-center" data-testid="document-viewer-error">
              <AlertTriangle className="mx-auto h-8 w-8 text-[#FF9000]" />
              <p className="mt-3 text-sm font-bold text-white">{error === 'EXTERNAL' ? "This file is hosted outside ATLAS and can't be previewed here." : error}</p>
              {error === 'EXTERNAL' && (
                <a href={doc.url} target="_blank" rel="noopener noreferrer" className="mt-5 inline-flex items-center gap-2 rounded-full bg-white px-5 py-2.5 text-xs font-extrabold text-[#0B1437]" data-testid="document-viewer-open-external">
                  <ExternalLink className="h-4 w-4" /> Open file
                </a>
              )}
            </div>
          ) : !blobUrl ? (
            <div className="flex h-full items-center justify-center" data-testid="document-viewer-loading">
              <Loader2 className="h-8 w-8 animate-spin text-[#23ACD5]" />
            </div>
          ) : isPdf ? (
            <Document
              file={pdfData}
              onLoadSuccess={({ numPages: n }) => setNumPages(n)}
              onLoadError={() => setError('This PDF could not be rendered. Use Download to open it on your device.')}
              loading={<div className="flex justify-center pt-20"><Loader2 className="h-8 w-8 animate-spin text-[#23ACD5]" /></div>}
              className="flex flex-col items-center gap-4"
            >
              {Array.from({ length: numPages }, (_, i) => (
                <div key={i + 1} ref={(el) => { pageRefs.current[i + 1] = el; }} className="overflow-hidden rounded-lg shadow-[0_12px_40px_rgba(0,0,0,0.45)]" data-testid={`document-viewer-page-${i + 1}`}>
                  <Page pageNumber={i + 1} width={pageWidth} scale={fitWidth ? 1 : zoom} renderTextLayer renderAnnotationLayer />
                </div>
              ))}
            </Document>
          ) : isImage ? (
            <div className="flex min-h-full items-center justify-center">
              <img src={blobUrl} alt={doc.title} style={fitWidth ? { maxWidth: '100%' } : { width: `${zoom * 100}%` }} className="rounded-lg shadow-2xl" data-testid="document-viewer-image" />
            </div>
          ) : (
            <div className="mx-auto mt-24 max-w-sm rounded-2xl border border-white/10 bg-white/5 p-8 text-center" data-testid="document-viewer-fallback">
              <FileText className="mx-auto h-10 w-10 text-[#23ACD5]" />
              <p className="mt-3 font-['Manrope'] text-base font-extrabold text-white">Preview not available</p>
              <p className="mt-1 text-xs text-white/55">{filename} can't be previewed in the browser. Download it to open on your device.</p>
              <button type="button" onClick={download} className="mt-5 inline-flex items-center gap-2 rounded-full bg-white px-5 py-2.5 text-xs font-extrabold text-[#0B1437]">
                <Download className="h-4 w-4" /> Download file
              </button>
            </div>
          )}
        </div>

        {/* Mobile pager */}
        {isPdf && numPages > 1 && (
          <div className="flex items-center justify-center gap-3 border-t border-white/10 bg-[#111C44] py-2 sm:hidden">
            <ToolbarButton onClick={() => goTo(page - 1)} disabled={page <= 1} title="Previous page" testId="document-viewer-mobile-prev"><ChevronLeft className="h-4 w-4" /></ToolbarButton>
            <span className="text-xs font-bold text-white/80" data-testid="document-viewer-mobile-page-indicator">{page} / {numPages}</span>
            <ToolbarButton onClick={() => goTo(page + 1)} disabled={page >= numPages} title="Next page" testId="document-viewer-mobile-next"><ChevronRight className="h-4 w-4" /></ToolbarButton>
          </div>
        )}
      </motion.div>
    </AnimatePresence>
  );
};

export const openExternalOrViewer = (openDocument, url, title) => {
  if (!url) return;
  if (/\.(pdf|png|jpe?g|webp|docx?|xlsx?|pptx?)(\?|$)/i.test(url) || url.includes('/api/documents/')) {
    openDocument({ url, title });
  } else {
    window.open(url, '_blank', 'noopener,noreferrer');
  }
};

